import { useState, type ReactNode } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  Typography,
  Box,
} from "@mui/material";
import { useCompanyInfoByNumber, type CompanyInfo } from "components/houjinApi/companyInfoApi";

interface CompanySearchModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (company: CompanyInfo) => void;
  initialSearchTerm?: string;
  subTitle?: ReactNode;
}

export default function CompanySearchByNumberModal({
  open,
  onClose,
  onSelect,
  initialSearchTerm = "",
  subTitle,
}: CompanySearchModalProps) {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [hasSearched, setHasSearched] = useState(false);
  const { getCompanyInfoByNumber, isLoading, error, companyInfoList } = useCompanyInfoByNumber();

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;

    try {
      await getCompanyInfoByNumber(searchTerm);
      setHasSearched(true);
    } catch (error) {
      console.error("Error searching for company:", error);
      setHasSearched(true);
    }
  };

  const handleSelect = (company: CompanyInfo) => {
    onSelect(company);
    onClose();
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>法人番号検索</DialogTitle>
      <DialogContent>
        {subTitle && (
          <Box sx={{ mb: 4 }}>
            {subTitle}
          </Box>
        )}
        <Box sx={{ display: "flex", mb: 2, mt: 1 }}>
          <TextField
            fullWidth
            label="法人番号"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            variant="outlined"
            size="small"
            sx={{ mr: 1 }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isLoading || !searchTerm.trim()}
          >
            検索
          </Button>
        </Box>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ my: 2 }}>
            エラーが発生しました: {error}
          </Typography>
        )}

        {hasSearched && !isLoading && companyInfoList.length === 0 && (
          <Typography sx={{ my: 2 }}>
            検索結果がありません。別の法人番号で検索してください。
          </Typography>
        )}

        {!isLoading && companyInfoList.length > 0 && (
          <List sx={{ maxHeight: "400px", overflow: "auto" }}>
            {companyInfoList.map((company, index) => (
              <ListItem key={index} disablePadding divider>
                <ListItemButton onClick={() => handleSelect(company)}>
                  <ListItemText
                    primary={company.clientCompanyName}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" display="block">
                          法人番号: {company.corporateNumber}
                        </Typography>
                        <Typography component="span" variant="body2" display="block">
                          〒{company.postalCode} {company.prefecture}
                          {company.city}
                          {company.address1}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>キャンセル</Button>
      </DialogActions>
    </Dialog>
  );
}
